import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Lock, Info } from 'lucide-react';
import { Reveal, Words, CountUp } from '../motion/Reveal';
import { ScoreGauge } from './ScoreGauge';

const H2 = 'text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight leading-tight';
const btn = 'group inline-flex items-center justify-center gap-2 bg-[#1a5c3b] hover:bg-[#165432] text-white font-bold px-8 py-4 rounded-full shadow-[0_12px_35px_rgba(26,92,59,0.3)] hover:scale-[1.03] transition-[transform,background-color]';

export const ScoreHero = ({ c }) => (
  <section className="relative overflow-hidden bg-gradient-to-br from-[#eef6f0] via-white to-[#e6f4ea] pt-10 md:pt-16 pb-16 md:pb-24" data-testid="score-hero">
    <div className="absolute inset-0 opacity-[0.35] pointer-events-none bg-[radial-gradient(#1a5c3b22_1px,transparent_1px)] [background-size:22px_22px]" />
    <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-emerald-200/50 blur-3xl pointer-events-none animate-blob" />
    <div className="absolute -bottom-40 -left-24 w-[380px] h-[380px] rounded-full bg-lime-100/60 blur-3xl pointer-events-none animate-blob [animation-delay:3s]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      <div className="lg:col-span-6">
        <Reveal as="span" className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#1a5c3b] bg-white border border-emerald-200 shadow-sm px-4 py-1.5 rounded-full mb-6">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{c.eyebrow}
        </Reveal>
        <Words as="h1" parts={[[c.h1a, 'text-transparent bg-clip-text bg-gradient-to-r from-[#1a5c3b] to-emerald-500'], [c.h1b]]} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-6" data-testid="score-heading" />
        <Reveal delay={350} className="w-16 h-1 rounded-full bg-[#1a5c3b] mb-6" />
        <Reveal as="p" delay={450} className="text-lg md:text-xl font-light text-slate-500 leading-relaxed mb-8">{c.sub}</Reveal>
        <Reveal delay={600} className="flex flex-wrap gap-4 items-center">
          <Link to="/verifier-admissibilite" className={btn} data-testid="score-start-btn">{c.start}<ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></Link>
          <Link to="/calculateur-pret-auto" className="font-semibold text-[#1a5c3b] hover:underline underline-offset-4" data-testid="score-calc-link">{c.calc}</Link>
        </Reveal>
      </div>
      <Reveal delay={250} className="lg:col-span-6"><ScoreGauge c={c} /></Reveal>
    </div>
  </section>
);

export const ScoreImpact = ({ c }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="score-impact">
    <div className="max-w-3xl mb-10">
      <Words parts={[[c.impactTitle]]} className={`${H2} mb-4`} />
      <Reveal as="p" delay={150} className="text-lg text-slate-600">{c.impactSub}</Reveal>
    </div>
    <div className="grid md:grid-cols-3 gap-5">
      {c.impact.map(([Icon, t, d], i) => (
        <Reveal key={t} delay={i * 150} className="group bg-white border border-emerald-100 rounded-3xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
          <div className="floaty" style={{ animationDelay: `${i * 0.6}s` }}><div className="w-14 h-14 rounded-2xl bg-[#1a5c3b] text-white flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-[-6deg] transition-transform"><Icon className="w-7 h-7" /></div></div>
          <h3 className="text-2xl font-bold mb-2">{t}</h3>
          <p className="text-slate-600 leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-12 relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] p-8 md:p-12 grid md:grid-cols-5 gap-6 items-center" data-testid="score-minimum">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:20px_20px]" />
      <div className="relative md:col-span-1 text-center">
        <p className="text-6xl md:text-7xl font-extrabold text-emerald-300 leading-none"><CountUp to={650} /><span className="text-4xl align-top">+</span></p>
      </div>
      <div className="relative md:col-span-4">
        <h3 className="text-2xl md:text-3xl font-bold !text-white mb-3">{c.minTitle}</h3>
        <p className="text-emerald-50/90 leading-relaxed mb-6">{c.minText}</p>
        <Link to="/verifier-admissibilite" className="inline-flex items-center gap-2 bg-white text-[#1a5c3b] font-bold px-7 py-3.5 rounded-full hover:scale-[1.03] transition-transform" data-testid="score-min-btn">{c.minBtn}<ArrowRight className="w-4 h-4" /></Link>
      </div>
    </Reveal>
  </section>
);

export const ScoreFactors = ({ c }) => (
  <section className="bg-gradient-to-b from-[#eef6f0] to-[#f8faf7] py-16 md:py-24" data-testid="score-factors">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <Words parts={[[c.factorsTitle]]} className={`${H2} mb-4 text-center`} />
      <Reveal as="p" delay={150} className="text-lg text-slate-600 text-center max-w-3xl mx-auto mb-12">{c.factorsSub}</Reveal>
      <div className="space-y-4">
        {c.factors.map(([Icon, t, pct, d], i) => (
          <Reveal key={t} delay={i * 100} className="group bg-white rounded-2xl border border-emerald-100 p-5 md:p-6 flex items-center gap-5 shadow-sm hover:shadow-lg transition-shadow" data-testid={`score-factor-${i + 1}`}>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-[#1a5c3b] flex items-center justify-center shrink-0 group-hover:bg-[#1a5c3b] group-hover:text-white transition-colors"><Icon className="w-6 h-6" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <p className="font-bold text-lg">{t}</p>
                <p className="text-2xl font-extrabold text-[#1a5c3b] tabular-nums">{pct}%</p>
              </div>
              <div className="h-2.5 bg-emerald-50 rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full bg-gradient-to-r from-[#1a5c3b] to-emerald-400 origin-left scale-x-0 group-[.in]:scale-x-100 transition-transform duration-1000" style={{ width: `${(pct / 35) * 100}%`, transitionDelay: `${300 + i * 100}ms` }} />
              </div>
              <p className="text-sm text-slate-600">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ScoreRebuild = ({ c }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-start" data-testid="score-rebuild">
    <div className="min-w-0">
      <Words parts={[[c.rebuildTitle]]} className={`${H2} mb-6`} />
      <Reveal as="p" delay={150} className="text-lg text-slate-600 leading-relaxed mb-6">{c.rebuildText}</Reveal>
      <Reveal delay={250} className="relative overflow-hidden mb-8 [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]" data-testid="score-situations-marquee">
        <div className="marquee-track flex gap-2 w-max">
          {[...c.situations, ...c.situations].map((s, i) => <span key={i} className="whitespace-nowrap text-sm font-semibold text-[#1a5c3b] bg-emerald-50 border border-emerald-100 px-4 py-2 rounded-full">{s}</span>)}
        </div>
      </Reveal>
      <Reveal delay={350}><Link to="/retablir-son-credit" className={btn} data-testid="score-rebuild-btn">{c.rebuildBtn}<ArrowRight className="w-5 h-5" /></Link></Reveal>
    </div>
    <div>
      <Reveal as="p" className="text-sm font-bold uppercase tracking-[0.2em] text-emerald-600 mb-6">{c.stepsTitle}</Reveal>
      <div className="relative space-y-8">
        <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-emerald-300 to-[#1a5c3b] opacity-40" />
        {c.steps.map(([t, d], i) => (
          <Reveal key={t} delay={150 + i * 180} className="relative flex gap-5 group" data-testid={`score-step-${i + 1}`}>
            <div className={`shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg ring-8 ring-white group-hover:scale-110 transition-transform ${['bg-emerald-400', 'bg-emerald-600', 'bg-[#1a5c3b]'][i]}`}>{i + 1}</div>
            <div className="pt-2"><h3 className="text-xl font-bold mb-1">{t}</h3><p className="text-slate-600">{d}</p></div>
          </Reveal>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-10">
        {c.trust.map(([t, d], i) => (
          <Reveal key={t} delay={300 + i * 120} className="flex gap-3 bg-[#f8faf7] rounded-2xl p-5">
            {i === 0 ? <ShieldCheck className="w-6 h-6 text-[#1a5c3b] shrink-0" /> : <Lock className="w-6 h-6 text-[#1a5c3b] shrink-0" />}
            <div><p className="font-bold">{t}</p><p className="text-sm text-slate-600">{d}</p></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ScoreCta = ({ c }) => (
  <section className="max-w-6xl mx-auto px-4 pb-20" data-testid="score-cta">
    <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#1a5c3b] to-[#0f3d26] px-8 py-12 md:px-14 md:py-16 text-center">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:20px_20px]" />
      <div className="relative">
        <Words parts={[[c.ctaTitle]]} className="text-3xl md:text-5xl font-bold !text-white mb-4" />
        <Reveal as="p" delay={200} className="text-emerald-100 text-base md:text-lg max-w-2xl mx-auto mb-8 flex items-center justify-center gap-2"><Info className="w-5 h-5 shrink-0" />{c.ctaText}</Reveal>
        <Link to="/verifier-admissibilite" className="inline-flex items-center justify-center gap-2 bg-white text-[#1a5c3b] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-lg" data-testid="score-cta-btn">{c.ctaBtn}<ArrowRight className="w-5 h-5" /></Link>
      </div>
    </div>
  </section>
);
